import { supabase } from './SupabaseConfig';
import { MenuRepository } from '../../domain/pos/ports/MenuRepository';
import { InfrastructureError } from '@core/errors/AppError';

export class SupabaseMenuRepository implements MenuRepository {
  async getMenu(uid: string): Promise<{
    menuItems: any[];
    menuCategories: any[];
    combos: any[];
    brandName?: string;
  }> {
    try {
      // 1. Get Brand Name from profiles
      const { data: profile } = await supabase
        .from('profiles')
        .select('business_name')
        .eq('id', uid)
        .maybeSingle();

      // 2. Get Menu info (active menu)
      const { data: menu } = await supabase
        .from('menus')
        .select('*')
        .eq('owner_id', uid)
        .eq('is_active', true)
        .maybeSingle();

      const menuId = menu?.id;

      // 3. Get Menu Items
      const { data: menuItems } = menuId 
        ? await supabase.from('menu_items').select('*').eq('menu_id', menuId)
        : { data: [] };

      // 4. Get Categories
      const { data: categories } = await supabase
        .from('menu_categories')
        .select('*')
        .eq('owner_id', uid);

      return {
        menuItems: menuItems || [],
        menuCategories: categories || [],
        combos: menu?.combos || [],
        brandName: profile?.business_name || ''
      };
    } catch (error) {
      throw new InfrastructureError('Lỗi lấy menu (Supabase)', 'Supabase', error as Error);
    }
  }

  async saveMenu(uid: string, data: {
    menuItems?: any[];
    menuCategories?: any[];
    combos?: any[];
    brandName?: string;
  }): Promise<void> {
    try {
      // 1. Update Brand Name if provided
      if (data.brandName !== undefined) {
        await supabase
          .from('profiles')
          .update({ business_name: data.brandName })
          .eq('id', uid);
      }

      // 2. Ensure we have an active menu for this owner
      let { data: menu } = await supabase
        .from('menus')
        .select('id')
        .eq('owner_id', uid)
        .eq('is_active', true)
        .maybeSingle();

      if (!menu) {
        const { data: newMenu, error: createError } = await supabase
          .from('menus')
          .insert({
            owner_id: uid,
            name: 'Default Menu',
            is_active: true,
            combos: data.combos || []
          })
          .select()
          .single();
        
        if (createError) throw createError;
        menu = newMenu;
      } else if (data.combos !== undefined) {
        await supabase
          .from('menus')
          .update({ combos: data.combos })
          .eq('id', menu.id);
      }
      if (!menu) {
        throw new InfrastructureError('Không thể tạo hoặc lấy menu hoạt động', 'Supabase');
      }

      const menuId = menu.id;

      // 3. Update Menu Items (Sync approach)
      if (data.menuItems) {
        // Simple strategy: Delete all and re-insert for the menu
        // In a production app, we'd do a proper upsert/delete diff
        await supabase.from('menu_items').delete().eq('menu_id', menuId);
        
        if (data.menuItems.length > 0) {
          const itemsToInsert = data.menuItems.map(item => ({
            ...item,
            menu_id: menuId,
            owner_id: uid // some tables might have owner_id for RLS
          }));
          await supabase.from('menu_items').insert(itemsToInsert);
        }
      }

      // 4. Update Categories
      if (data.menuCategories) {
        await supabase.from('menu_categories').delete().eq('owner_id', uid);
        if (data.menuCategories.length > 0) {
          const catsToInsert = data.menuCategories.map(cat => ({
            ...cat,
            owner_id: uid
          }));
          await supabase.from('menu_categories').insert(catsToInsert);
        }
      }

    } catch (error) {
      throw new InfrastructureError('Lỗi lưu menu (Supabase)', 'Supabase', error as Error);
    }
  }

  listenMenu(uid: string, callback: (data: { menuItems: any[]; menuCategories: any[] }) => void): () => void {
    const channel = supabase
      .channel(`menu-changes-${uid}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'menu_items', filter: `owner_id=eq.${uid}` },
        async () => {
          const updated = await this.getMenu(uid);
          callback({ menuItems: updated.menuItems, menuCategories: updated.menuCategories });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'menu_categories', filter: `owner_id=eq.${uid}` },
        async () => {
          const updated = await this.getMenu(uid);
          callback({ menuItems: updated.menuItems, menuCategories: updated.menuCategories });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }
}
